import type { JsonQuestionnaireSection, JsonQuestion } from './types';
import { buildQuestionIdToStorageKeyMap, getQuestionKey, resolveStorageKeyForQuestionId } from './questionKey';
import type { JsonAnswerValue } from './QuestionRenderer';
import {
  questionMatchesDemographic,
  showWhenConditionMatches,
  type QuestionnaireDemographicContext,
} from './clientContext';

function questionIdentifiers(question: JsonQuestion): string[] {
  return [
    getQuestionKey(question),
    question._id,
    question.questionId,
    question.externalId,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
}

function dependencyQuestionIds(question: JsonQuestion): string[] {
  const ids: string[] = [];
  if (question.showWhen?.questionId) ids.push(question.showWhen.questionId);
  for (const condition of question.showWhenAny ?? []) {
    if (condition?.questionId) ids.push(condition.questionId);
  }
  return ids;
}

function resolveParentQuestion(
  question: JsonQuestion,
  questionByIdentifier: Map<string, JsonQuestion>,
): JsonQuestion | null {
  const depIds = dependencyQuestionIds(question);
  let best: JsonQuestion | null = null;

  for (const depId of depIds) {
    const parent = questionByIdentifier.get(depId);
    if (!parent || parent === question) continue;
    if (!best || (parent.order ?? 0) > (best.order ?? 0)) {
      best = parent;
    }
  }

  return best;
}

function orderDependentsAfterParents(questions: JsonQuestion[]): JsonQuestion[] {
  if (questions.length <= 1) return questions;

  const indexByQuestion = new Map<JsonQuestion, number>();
  questions.forEach((question, index) => indexByQuestion.set(question, index));

  const questionByIdentifier = new Map<string, JsonQuestion>();
  for (const question of questions) {
    for (const id of questionIdentifiers(question)) {
      questionByIdentifier.set(id, question);
    }
  }

  const parentByChild = new Map<JsonQuestion, JsonQuestion>();
  const childrenByParent = new Map<JsonQuestion, JsonQuestion[]>();

  for (const question of questions) {
    const parent = resolveParentQuestion(question, questionByIdentifier);
    if (!parent) continue;
    parentByChild.set(question, parent);
    const children = childrenByParent.get(parent) ?? [];
    children.push(question);
    childrenByParent.set(parent, children);
  }

  for (const children of childrenByParent.values()) {
    children.sort((a, b) => (indexByQuestion.get(a) ?? 0) - (indexByQuestion.get(b) ?? 0));
  }

  const ordered: JsonQuestion[] = [];
  const visited = new Set<JsonQuestion>();
  const visiting = new Set<JsonQuestion>();

  const emit = (question: JsonQuestion) => {
    if (visited.has(question)) return;
    if (visiting.has(question)) {
      ordered.push(question);
      visited.add(question);
      return;
    }

    visiting.add(question);
    ordered.push(question);
    visited.add(question);

    for (const child of childrenByParent.get(question) ?? []) {
      emit(child);
    }

    visiting.delete(question);
  };

  for (const question of questions) {
    if (parentByChild.has(question)) continue;
    emit(question);
  }

  for (const question of questions) {
    emit(question);
  }

  return ordered;
}

/**
 * Preserve API question order, then keep dependents immediately after their
 * latest visible dependency. Section headers in the fill UI are driven by
 * consecutive `label` values, not section grouping.
 */
export function orderQuestionsBySectionLabel(questions: JsonQuestion[]): JsonQuestion[] {
  if (questions.length <= 1) return questions;
  return orderDependentsAfterParents(questions);
}

function selectedValuesFromAnswer(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof value === 'boolean') {
    return [value ? 'true' : 'false'];
  }
  if (typeof value === 'string' && value.trim().length > 0) return [value.trim()];
  return [];
}

/**
 * Returns ordered questions that are currently visible.
 * Visibility is evaluated in this order (see types.ts):
 * 1. If hideWhen: hide when answers[hideWhen.questionId] is in hideWhen.values.
 * 2. Else if showWhen: show only when answers[showWhen.questionId] is in showWhen.optionValues.
 * 3. Else if showWhenAny: show when any of those conditions match.
 * 4. Else: show.
 * Legacy: isConditional + medicalConditions still supported.
 */
export function getVisibleOrderedQuestions(
  section: JsonQuestionnaireSection,
  answers: Record<string, JsonAnswerValue>,
  demographicContext?: QuestionnaireDemographicContext
): JsonQuestion[] {
  const active = (section.questions ?? []).filter((q) => q.isActive !== false);
  const ordered = active.slice();
  const idToKey = buildQuestionIdToStorageKeyMap(section.questions);
  const answerForRefId = (questionId: string) =>
    answers[resolveStorageKeyForQuestionId(idToKey, questionId)];

  const canHaveSelectedValues = (q: JsonQuestion) =>
    q.type === 'multi-select' || q.type === 'select' || q.type === 'radio';

  const allSelectedValues = new Set<string>();
  for (const q of ordered) {
    if (!canHaveSelectedValues(q)) continue;
    const key = getQuestionKey(q);
    const vals = selectedValuesFromAnswer(answers[key]);
    for (const v of vals) allSelectedValues.add(v);
  }

  const conditionMatches = (cond: { questionId: string; optionValues: string[] }) =>
    showWhenConditionMatches(cond, answerForRefId, demographicContext, ordered);

  const valueInHideSet = (questionId: string, values: string[]) => {
    const depAnswer = answerForRefId(questionId);
    const selected = selectedValuesFromAnswer(depAnswer);
    const valueSet = new Set(values.map((v) => v.trim()));
    return selected.some((s) => valueSet.has(s.trim()));
  };

  const visible = ordered.filter((q) => {
    if (!questionMatchesDemographic(q, demographicContext)) return false;

    // 1. hideWhen: hide when ref answer is in hideWhen.values
    if (q.hideWhen) {
      if (valueInHideSet(q.hideWhen.questionId, q.hideWhen.values)) return false;
    }

    // 2. showWhen: show only when ref answer is in showWhen.optionValues
    if (q.showWhen) {
      if (!conditionMatches(q.showWhen)) return false;
    }

    // 3. showWhenAny: show when any of the conditions match (OR)
    if (q.showWhenAny && q.showWhenAny.length > 0) {
      const anyMatch = q.showWhenAny.some(conditionMatches);
      if (!anyMatch) return false;
    }

    // Legacy: isConditional + medicalConditions
    if (q.isConditional && q.medicalConditions?.length) {
      return q.medicalConditions.some((mc) => allSelectedValues.has(mc));
    }

    return true;
  });

  return orderQuestionsBySectionLabel(visible);
}
